<template>
  <div class="border-b border-gray-200 dark:border-gray-700">
    <!-- Tıklanabilir Sipariş Başlık Bilgileri (Çekmece Başlığı) -->
    <div
      class="p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors flex items-center justify-between"
      :class="order.hasCrisis ? 'bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500' : ''"
      @click.stop="toggleExpand"
    >
      <div class="flex-1">
        <div class="font-medium text-sm text-red-600 dark:text-red-400 flex items-center truncate">
          <span>{{ order.orderNo }} - {{ order.orderName }}</span>
          <!-- Kriz durumu badge'i -->
          <div
            v-if="order.hasCrisis"
            class="ml-2 flex items-center"
          >
            <span class="animate-pulse bg-red-600 text-white text-xs px-2 py-0.5 rounded-full flex items-center">
              <i class="fas fa-exclamation-triangle mr-1"></i>
              KRİZ
            </span>
          </div>
        </div>
        <div class="flex items-center flex-wrap mt-1">
          <div class="flex items-center mr-2 min-w-0 max-w-[45%]">
            <i class="fas fa-map-marker-alt text-red-400 mr-1 text-xs"></i>
            <span class="text-xs text-gray-600 dark:text-gray-300 truncate ">{{ order.receivedName }}</span>
          </div>
          <i class="fas fa-arrow-right text-gray-400 mx-1 text-xs flex-shrink-0"></i>
          <div class="flex items-center min-w-0 max-w-[45%]">
            <span class="text-xs text-gray-600 dark:text-gray-300 truncate whitespace-normal">{{ order.deliveredName }}</span>
          </div>
        </div>
        <div class="flex items-center mt-1 space-x-4">
          <div class="flex items-center">
            <i class="fas fa-weight text-red-500 mr-1 text-xs"></i>
            <span class="text-xs text-red-600 dark:text-red-400 font-medium">{{ order.tonnage }} {{ order.unitType }}</span>
          </div>

          <!-- Kalan Tonaj - Kriz durumunda göster -->
          <div v-if="order.remainingTonnage !== undefined && order.remainingTonnage < order.tonnage" class="flex items-center">
            <i class="fas fa-exclamation-circle mr-1 text-xs" :class="order.remainingTonnage <= 275 ? 'text-red-500' : 'text-amber-500'"></i>
            <span class="text-xs font-medium" :class="order.remainingTonnage <= 275 ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'">
              Kalan: {{ order.remainingTonnage }} TON
            </span>
          </div>

          <div class="flex items-center">
            <i class="far fa-calendar-alt text-gray-400 mr-1 text-xs"></i>
            <span class="text-xs text-gray-500">{{ formatDate(order.estimatedComplateDate) }}</span>
          </div>

          <!-- Araç Sayısı Bilgisi - Sadece araç varsa göster -->
          <div class="flex items-center" v-if="hasVehicles">
            <i class="fas fa-truck text-gray-400 mr-1 text-xs"></i>
            <span class="text-xs text-gray-500">
              {{ vehicleCount }} Benzersiz Araç
            </span>
          </div>
        </div>

        <!-- Onay bekleyen ve red edilen araçlar bilgisi -->
        <div v-if="order.pendingVehicleCount > 0 || order.rejectedVehicleCount > 0" class="flex items-center mt-1 space-x-3">
          <div v-if="order.pendingVehicleCount > 0" class="flex items-center">
            <i class="fas fa-clock text-amber-500 mr-1 text-xs"></i>
            <span class="text-xs text-amber-600 dark:text-amber-400">{{ order.pendingVehicleCount }} onay bekliyor</span>
          </div>
          <div v-if="order.rejectedVehicleCount > 0" class="flex items-center">
            <i class="fas fa-times-circle text-red-500 mr-1 text-xs"></i>
            <span class="text-xs text-red-600 dark:text-red-400">{{ order.rejectedVehicleCount }} red edildi</span>
          </div>
        </div>
      </div>
      
      <!-- Genişlet/Daralt İkonu -->
      <div class="flex-shrink-0 ml-2 text-gray-400">
        <i class="fas" :class="isExpanded ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
      </div>
    </div>

    <!-- Genişletilebilir İçerik (Çekmece İçeriği) -->
    <div v-if="isExpanded" class="pb-3 border-t border-gray-100 dark:border-gray-700">
      <!-- Sekme Butonları -->
      <div class="px-3 mt-3 mb-2 flex space-x-2">
        <button 
          @click.stop="$emit('tab-change', 'nearby')" 
          class="flex items-center px-3 py-1.5 text-sm rounded-md"
          :class="activeTab === 'nearby' ? 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-100' : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700/30'"
        >
          <i class="fas fa-map-marker-alt mr-2 text-xs" :class="activeTab === 'nearby' ? 'text-red-500' : 'text-gray-400'"></i>
          Yakındaki Araçlar
          <span v-if="order.nearbyVehicles?.length" class="ml-1">({{ order.nearbyVehicles.length }})</span>
        </button>
        
        <button 
          @click.stop="$emit('tab-change', 'recommended')" 
          class="flex items-center px-3 py-1.5 text-sm rounded-md"
          :class="activeTab === 'recommended' ? 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-100' : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700/30'"
        >
          <i class="fas fa-star mr-2 text-xs" :class="activeTab === 'recommended' ? 'text-red-500' : 'text-gray-400'"></i>
          Önerilen Araçlar
          <span v-if="order.recommendedVehicles?.length" class="ml-1">({{ order.recommendedVehicles.length }})</span>
        </button>
      </div>

      <!-- Araç Listeleri -->
      <div class="px-3">
        <!-- Yakındaki Araçlar Tab -->
        <live-assignment-vehicle-list
          v-if="activeTab === 'nearby'"
          :vehicles="order.nearbyVehicles"
          type="nearby"
          :loading="isLoadingNearby"
          @assign="(vehicleId) => $emit('assign', vehicleId)"
          @bulkAssign="(vehicles) => $emit('bulkAssign', vehicles)"

        />

        <!-- Önerilen Araçlar Tab -->
        <live-assignment-vehicle-list
          v-if="activeTab === 'recommended'"
          :vehicles="order.recommendedVehicles"
          type="recommended"
          :loading="isLoadingRecommended"
          @assign="(vehicleId) => $emit('assign', vehicleId)"
          @bulkAssign="(vehicles) => $emit('bulkAssign', vehicles)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import LiveAssignmentVehicleList from './LiveAssignmentVehicleList.vue';
import moment from 'moment';

export default {
  name: "LiveAssignmentOrderItem",
  components: {
    LiveAssignmentVehicleList
  },
  props: {
    order: {
      type: Object,
      required: true
    },
    activeTab: {
      type: String,
      default: 'nearby'
    },
    isLoadingNearby: {
      type: Boolean,
      default: false
    },
    isLoadingRecommended: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isExpanded: false // Varsayılan olarak kapalı başla
    };
  },
  computed: {
    // Toplam araç sayısı - benzersiz araçları sayarak
    vehicleCount() {
      // Iki listedeki tüm araçları birleştir
      const allVehicles = [
        ...(this.order.nearbyVehicles || []),
        ...(this.order.recommendedVehicles || [])
      ];
      
      // Benzersiz araç ID'lerini bul
      const uniqueVehicleIds = new Set();
      allVehicles.forEach(vehicle => {
        if (vehicle && vehicle.id) {
          uniqueVehicleIds.add(vehicle.id);
        }
      });
      
      // Benzersiz araç sayısını döndür
      return uniqueVehicleIds.size;
    },
    // Hiç araç var mı
    hasVehicles() {
      return this.vehicleCount > 0;
    },
    // Kriz durumu için tooltip metni
    crisisTooltipText() {
      if (!this.order.crisisStatus && !this.order.remainingTonnage) return '';

      // Kalan tonaj bazlı kriz durumu
      if (this.order.remainingTonnage && this.order.remainingTonnage <= 275) {
        return `Kritik Seviye: Sadece ${this.order.remainingTonnage} ton kaldı! ` +
               `${this.order.pendingVehicleCount > 0 ? this.order.pendingVehicleCount + ' araç onay bekliyor. ' : ''}` +
               `${this.order.rejectedVehicleCount > 0 ? this.order.rejectedVehicleCount + ' araç reddetti.' : ''}`;
      }

      // Araç sayısı bazlı kriz durumu (eski mantık)
      if (this.order.crisisStatus) {
        const status = this.order.crisisStatus;
        return `Kriz Durumu: İhtiyaç duyulan araç sayısı (${status.requiredVehicles}), ` +
               `mevcut araç sayısının (${status.assignedVehicles}) %10'undan fazla. ` +
               `Acil araç ataması gerekli!`;
      }

      return '';
    }
  },
  methods: {
    formatDate(date) {
      if (!date) return '-';
      return moment(date).format('DD.MM.YYYY');
    },
    toggleExpand() {
      // Event propagation'u durdur
      this.isExpanded = !this.isExpanded;

      // DOM güncellemesi için bekleme
      this.$nextTick(() => {
        if (this.isExpanded) {
          // Genişletildiğinde scroll ederek görünürlüğü sağla
          const element = this.$el;
          if (element) {
            // Üst container bulunamıyorsa, en yakın scroll container'i bul
            const scrollContainer = element.closest('.scrollable-container, .overflow-y-auto');
            if (scrollContainer) {
              const elementTop = element.offsetTop;
              const containerScrollTop = scrollContainer.scrollTop;
              const containerHeight = scrollContainer.clientHeight;
              
              // Elemanin container içindeki pozisyonunu kontrol et
              if (elementTop < containerScrollTop || 
                  elementTop > containerScrollTop + containerHeight) {
                // Görünür alan dışındaysa, o elemana scroll et
                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            }
          }
        }
      });
    },
    // Dışarıdan çağrılabilir expand metodu
    expand() {
      this.isExpanded = true;
    },
    // Dışarıdan çağrılabilir collapse metodu
    collapse() {
      this.isExpanded = false;
    }
  }
};
</script>

<style scoped>
/* Geçiş animasyonu - daha yumuşak scroll için düzenlendi */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease, max-height 0.3s ease;
  overflow: hidden;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
  max-height: 0;
}

/* Genişleyen öğeler için scroll davranışını belirle */
.expanded-content {
  will-change: height, max-height;
  transition: height 0.3s ease;
}
</style>